<template>
    <side-panel @close="$emit('close')">
        <!-- HEADER -->
        <template #header>
            <h3 class="m-0">{{ $l10n.__('Compte Bancaire') }}</h3>
        </template>

        <!-- CONTENT -->
        <template #content>
            <form class="bank-form-slider relative">
                <CompassMenu
                    v-if="needDropDown"
                    trigger="hover"
                    customClass="bank-form__menu"
                    class="absolute top-right"
                >
                    <CompassPlainButton :label="$l10n.__('Options')" />
                    <template #dropdown>
                        <CompassMenuItem
                            v-if="bankId !== null"
                            :label="$l10n.__('Supprimer')"
                            :disabled="!isDeletable"
                            @click="window.RankedList.deleteBankAccount(bankId)"
                        />
                        <CompassMenuItem
                            v-if="bankAccount.isBudgetInsight"
                            :label="$l10n.__('Gérer mes connexions')"
                            @click="window.BankReconciliation.redirectBudgetInsight()"
                        />
                    </template>
                </CompassMenu>

                <!-- ACCOUNT -->
                <section class="mb-4">
                    <h4 class="mt-0">{{ $l10n.__('Compte') }}</h4>

                    <!-- ! BUDGETINSIGHT -->
                    <div v-if="!bankAccount.isBudgetInsight">
                        <div class="label-input">
                            <label for="label">{{ $l10n.__('Banque') }}</label
                            ><br />
                            <el-input
                                id="label"
                                v-model="bankAccount.label"
                                :class="[{ 'has-error': 'label' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'label' in fieldInError" class="error-msg">
                                {{ fieldInError.label }}
                            </div>
                        </div>

                        <div class="label-input">
                            <label for="name">{{ $l10n.__('Nom du compte') }}</label
                            ><br />
                            <el-input
                                id="name"
                                v-model="bankAccount.name"
                                :class="[{ 'has-error': 'name' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'name' in fieldInError" class="error-msg">
                                {{ fieldInError.name }}
                            </div>
                        </div>
                    </div>

                    <!-- BUDGET INSIGHT -->
                    <div v-if="bankAccount.isBudgetInsight">
                        <div class="label-input">
                            <label for="bank">{{ $l10n.__('Banque') }}</label
                            ><br />
                            <el-input id="bank" v-model="bankAccount.bankName" disabled> </el-input>
                        </div>

                        <div class="label-input">
                            <label for="account">{{ $l10n.__('Compte') }}</label
                            ><br />
                            <el-input id="account" v-model="bankAccount.accountName" disabled>
                            </el-input>
                        </div>

                        <div class="label-input">
                            <label for="label">{{ $l10n.__('Alias') }}</label
                            ><br />
                            <el-input
                                id="label"
                                v-model="bankAccount.label"
                                :class="[{ 'has-error': 'label' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'label' in fieldInError" class="error-msg">
                                {{ fieldInError.label }}
                            </div>
                        </div>
                    </div>

                    <bank-status v-if="status != null" :status="status" />
                </section>

                <!-- ACCOUNT INFOS -->
                <section class="mb-4">
                    <h4 class="mt-0">{{ $l10n.__('Informations Compte') }}</h4>

                    <!-- IBAN TOGGLER -->
                    <div class="acenter">
                        <p for="label" class="fs-3 m-0 aleft">{{ $l10n.__('Compte avec IBAN') }}</p>
                        <el-radio-group v-model="bankAccount.hasiban" class="mt-2 mb-3">
                            <el-radio-button label="Y">{{ $l10n.__('Oui') }}</el-radio-button>
                            <el-radio-button label="N">{{ $l10n.__('Non') }}</el-radio-button>
                        </el-radio-group>
                    </div>

                    <!-- IBAN -->
                    <div v-if="bankAccount.hasiban == 'Y'">
                        <div class="label-input">
                            <label for="iban">{{
                                $l10n.__('International Bank Account Number (IBAN)')
                            }}</label
                            ><br />
                            <el-input
                                id="iban"
                                v-model="bankAccount.iban"
                                :class="[{ 'has-error': 'iban' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'iban' in fieldInError" class="error-msg">
                                {{ fieldInError.iban }}
                            </div>
                        </div>

                        <div class="label-input">
                            <label for="bic">{{ $l10n.__('Bank Identifier Code (BIC)') }}</label
                            ><br />
                            <el-input
                                id="bic"
                                v-model="bankAccount.bic"
                                :class="[{ 'has-error': 'bic' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'bic' in fieldInError" class="error-msg">
                                {{ fieldInError.bic }}
                            </div>
                        </div>

                        <div class="label-input">
                            <label for="sepa">{{
                                $l10n.__("Numéro national d'émetteur SEPA")
                            }}</label
                            ><br />
                            <el-input
                                id="sepa"
                                v-model="bankAccount.sepa"
                                :class="[{ 'has-error': 'sepa' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'sepa' in fieldInError" class="error-msg">
                                {{ fieldInError.sepa }}
                            </div>
                        </div>
                    </div>

                    <!-- ! IBAN -->
                    <div v-if="bankAccount.hasiban == 'N'">
                        <div class="label-input">
                            <label for="number">{{ $l10n.__('N° - du compte') }}</label
                            ><br />
                            <el-input
                                id="number"
                                v-model="bankAccount.number"
                                :class="[{ 'has-error': 'number' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'number' in fieldInError" class="error-msg">
                                {{ fieldInError.number }}
                            </div>
                        </div>

                        <div class="label-input">
                            <label for="rounting">{{ $l10n.__('Numéro de routage') }}</label
                            ><br />
                            <el-input
                                id="rounting"
                                v-model="bankAccount.rounting"
                                :class="[{ 'has-error': 'rounting' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'rounting' in fieldInError" class="error-msg">
                                {{ fieldInError.rounting }}
                            </div>
                        </div>

                        <div class="label-input">
                            <label for="swift">{{ $l10n.__('SWIFT') }}</label
                            ><br />
                            <el-input
                                id="swift"
                                v-model="bankAccount.swift"
                                :class="[{ 'has-error': 'swift' in fieldInError }]"
                            >
                            </el-input>
                            <div v-if="'swift' in fieldInError" class="error-msg">
                                {{ fieldInError.swift }}
                            </div>
                        </div>
                    </div>
                </section>

                <!-- ACCOUNTABILITY -->
                <section class="mb-4">
                    <h4 v-if="bankCodes.length > 0" class="mt-0">
                        {{ $l10n.__('Informations Comptabilité') }}
                    </h4>

                    <div v-if="bankCodes.length > 0" class="label-input">
                        <label for="accountingCode">{{ $l10n.__('Code comptabilité') }}</label
                        ><br />
                        <el-select
                            id="accountingCode"
                            v-model="bankAccount.accountingCode"
                            :class="hasError('accountingCode')"
                            class="wp-10"
                        >
                            <el-option
                                v-for="code in bankCodes"
                                :key="code.id"
                                :class="[{ 'has-error': 'accountingCode' in fieldInError }]"
                                :label="code.code + ' - ' + code.label"
                                :value="code.id"
                            >
                            </el-option>
                        </el-select>
                        <br />
                        <div v-if="'accountingCode' in fieldInError" class="error-msg">
                            {{ fieldInError.accountingCode }}
                        </div>
                    </div>

                    <div v-if="bankCodes.length > 0" class="label-input">
                        <label for="journalCode">{{ $l10n.__('Code journal de banque') }}</label
                        ><br />
                        <el-input
                            id="journalCode"
                            v-model="bankAccount.journalCode"
                            :class="[{ 'has-error': 'journal' in fieldInError }]"
                        >
                        </el-input
                        ><br />
                        <div v-if="'journal' in fieldInError" class="error-msg">
                            {{ fieldInError.journal }}
                        </div>
                    </div>
                </section>

                <!-- VISIBILITY -->
                <section class="mb-4">
                    <h4 class="mt-0">{{ $l10n.__('Visibilité') }}</h4>

                    <div class="label-input">
                        <el-checkbox v-model="bankAccount.isEnabled">{{
                            $l10n.__('Ce compte apparait dans Sellsy')
                        }}</el-checkbox>
                    </div>

                    <div v-if="bankAccount.isBudgetInsight" class="label-input">
                        <label for="retention">
                            {{ $l10n.__("Date de début d'affichage des transactions") }}</label
                        ><br />
                        <el-date-picker
                            id="retention"
                            v-model="bankAccount.retentionDate"
                            :class="[{ 'has-error': 'retentionDate' in fieldInError }]"
                            :format="datePickerFormat"
                            valueFormat="YYYY-MM-DD"
                            type="date"
                        >
                        </el-date-picker>
                        <br />
                        <div v-if="'retentionDate' in fieldInError" class="error-msg">
                            {{ fieldInError.retentionDate }}
                        </div>
                    </div>
                </section>
            </form>
        </template>

        <!-- FOOTER -->
        <template #footer>
            <CompassButton :label="$t.__('common.actions.save')" @click.prevent="save" />
        </template>
    </side-panel>
</template>

<script>
import SidePanel from 'vcomponents/utilities/SidePanel.vue';
import BankStatus from './BankStatus.vue';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters } = createNamespacedHelpers('corp');

export default {
    components: {
        SidePanel,
        BankStatus,
    },

    props: {
        bankId: {
            type: Number,
            required: false,
        },
        defaultBankCode: {
            type: Number,
            required: true,
        },
        bankCodes: {
            type: Array,
            required: true,
        },
        status: {
            type: String,
            required: false,
        },
    },
    data: function () {
        return {
            fieldsError: [],
            bankAccount: {
                label: null,
                hasiban: 'Y',
                iban: null,
                bic: null,
                sepa: null,
                name: null,
                number: null,
                rounting: null,
                swift: null,
                accountingCode: this.defaultBankCode + '',
                journalCode: null,
                isEnabled: true,
                retentionDate: null,
                isBudgetInsight: false,
                bankName: null,
                accountName: null,
            },
            isDeletable: false,
        };
    },
    computed: {
        fieldInError: function () {
            const fields = {};
            this.fieldsError.forEach((field) => {
                const fieldName = field.fieldId.split('_');
                fields[fieldName[1]] = field.msg;
            });
            return fields;
        },
        needDropDown: function () {
            return this.bankId != null || this.bankAccount.isBudgetInsight;
        },

        ...mapGetters(['datePickerFormat']),
    },
    mounted: function () {
        if (this.bankId != null) {
            const options = {
                prefsBank_id: this.bankId,
            };

            $.post(
                '/?_f=bankAccountPreferences&action=getOneBankAccount',
                options,
                (data) => {
                    this.bankAccount = data.bankAccount;
                    this.isDeletable = 'Y' == data.bankAccount.isDeletable;
                },
                'json',
            );
        }
    },
    methods: {
        hasError(fieldName) {
            return {
                'has-error': fieldName in this.fieldsError,
            };
        },
        save() {
            const options = {
                prefsBank_label: this.bankAccount.label,
                prefsBank_name: this.bankAccount.name,
                prefsBank_hasiban: this.bankAccount.hasiban,
                prefsBank_isEnabled: this.bankAccount.isEnabled ? 'Y' : 'N',
            };

            if (this.bankAccount.hasiban == 'Y') {
                ((options.prefsBank_iban = this.bankAccount.iban),
                (options.prefsBank_bic = this.bankAccount.bic),
                (options.prefsBank_sepa_transmitterNationalNumber = this.bankAccount.sepa));
            } else {
                ((options.prefsBank_number = this.bankAccount.number),
                (options.prefsBank_rounting = this.bankAccount.rounting));
                options.prefsBank_swift = this.bankAccount.swift;
            }

            if (this.bankCodes.length > 0) {
                options.prefsBank_accountingCode = this.bankAccount.accountingCode;
                options.prefsBank_journal_code = this.bankAccount.journalCode;
            }

            if (this.bankAccount.isBudgetInsight) {
                options.prefsBank_is_budgetInsight = 'Y';
                options.prefsBank_retentionDate = this.bankAccount.retentionDate;
            }

            let action = 'createBankAccount';
            if (this.bankId != null) {
                action = 'updateBankAccount';
                options.prefsBank_id = this.bankId;
            }

            $.post(
                '/?_f=bankAccountPreferences&action=' + action,
                options,
                (data) => {
                    this.fieldsError = data.form.fields;

                    if (this.fieldsError.length == 0) {
                        location.reload();
                    }
                },
                'json',
            );
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.bank-form-slider {
    .has-error input {
        border-color: $red;
    }
    .error-msg {
        color: $red;
    }

    // Reset CSS to element.io
    .el-input__prefix,
    .el-input__suffix {
        top: -5px;
    }
    #retention {
        width: 220px;
        padding: 0 30px;
    }
}
#compass-menu-container {
    .bank-form__menu {
        z-index: 1001;
    }
}
</style>
